<template>
  <a-drawer
    :visible="visible"
    title="Selecciona tus filtros avanzados"
    destroy-on-close
    placement="right"
    width="480"
    :body-style="{ padding: 0, height: 'calc(100% - 55px)' }"
    @close="handleCloseDrawer"
  >
    <custom-scrollbar height="calc(100% - 72px)">
      <div class="modal__body">
        <p class="drawer__body--subtitle">
          Filtra tus correos en base a sus gestiones realizadas. Puedes complementalo seleccionando
          una empresa, campaña o base.
        </p>

        <div v-if="!empty">
          <a-form layout="vertical" ref="formRef" :model="formState">
            <a-form-item label="Todos los identificadores">
              <a-select
                :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                v-model:value="formState.identifiers"
                :options="options.identifiers"
                placeholder="Seleccione"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Estado de emails">
              <a-select
                :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                v-model:value="formState.emailStates"
                :options="options.emailStates"
                mode="tags"
                placeholder="Seleccione"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Etiquetas">
              <a-select
                :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                v-model:value="formState.tags"
                :options="options.tags"
                mode="tags"
                placeholder="Seleccione"
                style="width: 100%"
              />
            </a-form-item>

            <template v-if="userData?.profile !== ProfileTypes.Agent">
              <p class="drawer__body--title">Selecciona los usuarios en gestión:</p>
              <agents-picker
                :value="formState.users"
                @update:value="formState.users = $event"
                :items="emailsAdvancedFiltersData.users || []"
              />
            </template>
          </a-form>
        </div>

        <div class="container__empty" v-else>
          <img :src="maskGroupImage" alt="Vacio" />
          <p class="text--top">
            Podrás utilizar esta opción cuando tengas tickets históricos o activos
          </p>
        </div>
      </div>
    </custom-scrollbar>

    <div class="footer">
      <a-button class="footer__button" @click="handleClearFilters"> Limpiar </a-button>
      <a-button type="primary" @click="handleApplyFilters"> Aplicar </a-button>
    </div>
  </a-drawer>
</template>
<script setup lang="ts">
import { computed, ref, createVNode } from "vue";
import { useStore } from "vuex";
import { Modal, message } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { ProfileTypes, EmailState } from "@/app/shared/utils/enums";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import AgentsPicker from "@/app/shared/components/pickers/AgentsPicker.vue";

import maskGroupImage from "@/app/contacts/assets/maskGroup.svg?url";

const props = defineProps({
  visible: {
    required: true,
    type: Boolean,
  },
  empty: {
    required: false,
    type: Boolean,
    default: false,
  },
  selectedFilters: {
    required: false,
    type: Object,
  },
});

const emit = defineEmits(["clear-filters", "apply-filters", "close-drawer"]);

const store = useStore();

const emailsAdvancedFiltersData = computed(() => store.getters["getEmailsAdvancedFiltersData"]);
const userData = computed(() => store.getters["getDataUser"]);

const formRef = ref(null);
const formState = ref({
  identifiers: props.selectedFilters?.identifiers || null,
  emailStates: props.selectedFilters?.emailStates || [],
  tags: props.selectedFilters?.tags || [],
  users: props.selectedFilters?.users
    ? emailsAdvancedFiltersData.value.users.filter((user) =>
        props.selectedFilters?.users.includes(user.id)
      )
    : [],
  contact_types: props.selectedFilters?.contact_types || [],
});

let options = {};

if (emailsAdvancedFiltersData.value) {
  options = {
    emailStates: [
      { value: EmailState.try, label: "En Bandeja" },
      { value: EmailState.assigned, label: "Asignado" },
      { value: EmailState.resolved, label: "Resuelto" },
    ],
    tags: emailsAdvancedFiltersData.value.tags.map((tag) => ({
      value: tag.id,
      label: tag.name,
    })),
    identifiers: [
      { value: null, label: "Todos los identificadores" },
      ...emailsAdvancedFiltersData.value.identifiers.map((identifier) => ({
        value: identifier.id,
        label: identifier.alias,
      })),
    ],
  };
}

const handleApplyFilters = () => {
  formRef.value.validateFields().then(() => {
    const { users, ...filters } = formState.value;

    const formFields = {
      ...filters,
      users: users.map((user) => user.id),
    };

    Object.values(formFields).flat().length === 0
      ? emit("clear-filters")
      : emit("apply-filters", formFields);
  });
};

const handleClearFilters = () => {
  emit("clear-filters");
  message.success("Se han limpiado los filtros correctamente");

  emit("close-drawer");
};

const handleCloseDrawer = () => {
  if (Object.values(formState.value).flat().length !== 0) {
    Modal.confirm({
      title: `¿Deseas descartar los cambios realizados?`,
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      icon: createVNode(QuestionCircleOutlined),
      centered: true,
      onOk: () => emit("close-drawer"),
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
    });
  } else {
    emit("close-drawer");
  }
};
</script>

<style lang="sass" scoped>
.modal
  &__body
    margin: 12px 24px

.footer
  display: flex
  align-items: center
  justify-content: flex-end
  margin-top: 8px
  padding: 12px 16px
  border-top: $border-normal

  &__display
    width: 100%
    font-size: 12px
    color: $gray-7

  &__button
    margin-right: 12px

.drawer__body
  &--title
    font-weight: 600
    color: $gray-9
    margin-bottom: 0

  &--subtitle
    font-weight: 400
    color: $gray-8

.container__empty
  text-align: center
  display: flex
  flex-direction: column
  height: calc(100% - 60px)
  align-items: center
  justify-content: center

  img
    width: 100px

.text--bottom
  margin-top: 0

.text--top
  margin-top: 20px
  margin-bottom: 0
</style>
